import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { PictureHtml } from '../../../../../packages/frontend/html/PictureHtml.js';
import { PictureSrcHtml } from '../../../../../packages/frontend/html/PictureSrcHtml.js';

@customElement('lit-picture-tile')
export class LitPictureTile extends LitElement {
    connectedCallback() {
        this.querySelectorAll(':scope > [data-lit-fallback]').forEach(element => element.remove());
        super.connectedCallback();
    }

    @property({ type: Object }) picture?: PictureHtml;
    @property({ type: String }) href?: string;
    @property({ type: Boolean }) debug = false;
    @property({ type: Boolean }) animated = true;
    @property({ type: Boolean }) isPrinting = false;
    @property({ type: Boolean, attribute: 'auto-reveal' }) autoReveal = true;

    @state() private isRevealed = false;
    // Szerokość .image-container w px CSS - wartość sizes. null = jeszcze nie
    // zmierzona: obraz nie jest renderowany, żeby przeglądarka nie pobrała
    // wariantu dobranego do całej szerokości okna (kafelek ma ~240-280 px).
    @state() private tileWidth: number | null = null;

    private resizeObserver?: ResizeObserver;

    createRenderRoot() {
        return this;
    }

    firstUpdated() {
        const container = this.querySelector<HTMLElement>('.image-container');
        if (!container) return;
        const measure = () => {
            const width = Math.ceil(container.getBoundingClientRect().width);
            if (width > 0) this.tileWidth = width;
        };
        measure();
        this.resizeObserver = new ResizeObserver(measure);
        this.resizeObserver.observe(container);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
    }

    /* ===============================
       IMAGE LOAD → REVEAL
    =============================== */
    private handleImageLoad = () => {
        if (!this.autoReveal || !this.animated) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.isRevealed = true;
            });
        });

        if (this.debug) {
            console.log('Reveal triggered after image load');
        }
    };

    render() {
        if (!this.picture) {
            return html`<div class="tile-error">Brak obrazu</div>`;
        }

        const srcSet = new PictureSrcHtml(this.picture.pictureData).getSrcSet();

        const classes = [
            'tile',
            this.animated ? 'animated' : '',
            this.autoReveal ? 'auto-reveal' : '',
            this.isRevealed ? 'revealed' : '',
        ]
            .filter(Boolean)
            .join(' ');

        const content = html`
            <div class="${classes}">
                <div class="image-container">
                    ${
                        this.tileWidth === null
                            ? ''
                            : html`<picture>
                              ${
                                  srcSet.avif
                                      ? html`<source srcset="${srcSet.avif}" sizes="${this.tileWidth}px" type="image/avif" />`
                                      : ''
                              }
                              ${
                                  srcSet.webp
                                      ? html`<source srcset="${srcSet.webp}" sizes="${this.tileWidth}px" type="image/webp" />`
                                      : ''
                              }
                              <img
                                  src="${srcSet.fallbackSrc}"
                                  srcset="${srcSet.jpg || ''}"
                                  sizes="${this.tileWidth}px"
                                  alt="${this.picture.alt || ''}"
                                  loading="lazy"
                                  decoding="async"
                                  @load="${this.handleImageLoad}"
                              />
                          </picture>`
                    }
                </div>
            </div>
        `;

        if (this.href && !this.isPrinting) {
            return html`<a href="${this.href}">${content}</a>`;
        }

        return content;
    }
}
